import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const { FiTrash2, FiX, FiAlertTriangle } = FiIcons;

const DeleteConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  petName, 
  isDeleting = false 
}) => {
  const { t } = useLanguage();

  // 🎯 修復背景滾動問題 - 完全鎖定背景滾動
  useEffect(() => {
    if (isOpen) {
      // 保存原始樣式和滾動位置
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalLeft = document.body.style.left;
      const originalRight = document.body.style.right;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // 獲取當前滾動位置
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // 計算滾動條寬度以防止頁面跳動
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // 完全鎖定背景滾動
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.height = '100vh';
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      
      // 防止各種形式的滾動
      const preventScroll = (e) => {
        // 檢查事件目標是否在模態框內容區域內
        const modalContent = document.querySelector('.delete-modal-content');
        if (modalContent && !modalContent.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      const preventKeyScroll = (e) => {
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Space'];
        const modalContent = document.querySelector('.delete-modal-content');
        if (scrollKeys.includes(e.key) && modalContent && !modalContent.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };
      
      // 添加事件監聽器
      document.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('keydown', preventKeyScroll, { passive: false });
      document.addEventListener('scroll', preventScroll, { passive: false });
      
      // 清理函數
      return () => {
        // 恢復所有原始樣式
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.left = originalLeft;
        document.body.style.right = originalRight;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
        document.body.style.paddingRight = originalPaddingRight;
        
        // 恢復滾動位置
        window.scrollTo(scrollX, scrollY);
        
        // 移除事件監聽器
        document.removeEventListener('touchmove', preventScroll);
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('keydown', preventKeyScroll);
        document.removeEventListener('scroll', preventScroll);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
        style={{ 
          touchAction: 'none',
          overscrollBehavior: 'none'
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="delete-modal-content bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          style={{ 
            touchAction: 'auto',
            overscrollBehavior: 'contain'
          }}
        >
          {/* 頭部區域 - 紅色漸變背景 */}
          <div className="relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 p-6 text-white">
            {/* 關閉按鈕 */}
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiX} className="h-5 w-5" />
            </button>

            {/* 警告圖標和標題 */}
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">
                  {t('modals.confirmDelete')}
                </h3>
                <p className="text-red-100 text-sm">
                  {t('modals.cannotUndo')}
                </p>
              </div>
            </div>
          </div>

          {/* 內容區域 */}
          <div className="p-6">
            {/* 主要訊息 */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-red-50 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrash2} className="h-10 w-10 text-red-500" />
              </div>
              
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                {t('modals.deletePetData')}
              </h4>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-4">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t('modals.confirmDeletePetMessage', { petName: petName || t('dashboard.unnamedPet') })}
                </p>
              </div>

              {/* 警告列表 */}
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-left">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                  <div className="text-sm text-red-700 dark:text-red-300">
                    <p className="font-semibold mb-2">
                      {t('modals.willLoseAfterDelete')}
                    </p>
                    <ul className="space-y-1 text-xs">
                      <li>• {t('modals.allBasicData')}</li>
                      <li>• {t('modals.healthRecordsAndVaccines')}</li>
                      <li>• {t('modals.careInstructionsAndNotes')}</li>
                      <li>• {t('modals.uploadedPhotos')}</li>
                      <li>• {t('modals.shareLinksWillExpire')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 操作按鈕 */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* 取消按鈕 */}
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <SafeIcon icon={FiX} className="h-5 w-5" />
                <span className="font-semibold">
                  {t('modals.cancel')}
                </span>
              </button>

              {/* 確認刪除按鈕 */}
              <button
                onClick={onConfirm}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    <span className="font-semibold">
                      {t('modals.deleting')}
                    </span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiTrash2} className="h-5 w-5" />
                    <span className="font-semibold">
                      {t('modals.confirmDelete')}
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* 底部提示 */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('modals.deleteWarningMessage')}
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DeleteConfirmModal;